/**
 * Turning "the list should look like this" into "put this card next to that one".
 *
 * ADR-067 Decision 6 replaced `PATCH /decks/{id}/cards/reorder` — a list of
 * `{id, position}` the client computed over the cards it happened to have
 * loaded — with `PATCH /decks/{id}/cards/{card_id}/move`, which takes a
 * placement relative to a sibling. Absolute positions were a claim about the
 * WHOLE deck that a client holding 30 of 1029 cards was in no position to make;
 * a relative placement is a claim about two cards, and is true or false on its
 * own.
 *
 * Every reorder surface in this app moves ONE card at a time — the up/down
 * buttons, drag-and-drop, insert-at-index on create, the mobile Reorder screen.
 * So a full-list reorder never has to be expressed at all: each of them is a
 * single relative move, and no sequence of moves is needed to stand in for one.
 *
 * The placement this returns deliberately never uses `start` or `end`. Those
 * are absolute — they mean the start and end of the DECK, not of the window the
 * caller can see — and the feed's window is not always a prefix (a deep link
 * anchors it mid-deck), so "dropped at the top of what I can see" is not
 * "dropped at the top of the deck". Anchoring on a neighbour that is on screen
 * says exactly what the user did and nothing more, whatever lies outside the
 * window.
 */
export type CardPlacement =
  | { placement: 'before' | 'after'; anchor_card_id: string }
  | { placement: 'start' | 'end' }

/**
 * The placement that reproduces `after` — the id list as it should read once
 * the move lands — for the single card `movedId`.
 *
 * `after` must be a contiguous window of the deck in canonical order (which is
 * what the feed's pages concatenate to). Returns `null` when the move cannot be
 * expressed against a neighbour: a one-card window has no neighbour to anchor
 * on, and a card that is already where it is asked to go needs no request.
 */
export function relativePlacement(after: string[], movedId: string): CardPlacement | null {
  const i = after.indexOf(movedId)
  if (i === -1) return null
  if (i > 0) return { placement: 'after', anchor_card_id: after[i - 1] }
  if (after.length > 1) return { placement: 'before', anchor_card_id: after[1] }
  return null
}
